import { Check, CheckCheck } from 'lucide-react';
import React from 'react';
import aliceAvatar from '../../assets/avatars/alice.svg';

/**
 * Modern Chat Message Component
 * Based on conversation-mockup.html design
 *
 * @param {Object} props
 * @param {string} props.message - Message content
 * @param {string} props.sender - 'alice' | 'user'
 * @param {string} props.timestamp - Message timestamp
 * @param {string} props.status - 'sent' | 'delivered' | 'read'
 * @param {boolean} props.isTyping - Show typing indicator
 */
export function ChatMessage({
  message,
  sender,
  timestamp,
  status = 'sent',
  isTyping = false
}) {
  const isAlice = sender === 'alice';
  const isUser = sender === 'user';

  // Status icon component
  const StatusIcon = () => {
    switch (status) {
      case 'delivered':
        return <Check className="w-3 h-3 text-gray-400" />;
      case 'read':
        return <CheckCheck className="w-3 h-3 text-green-500" />;
      default:
        return <Check className="w-3 h-3 text-gray-400" />;
    }
  };

  // Typing indicator component
  const TypingIndicator = () => (
    <div className="flex items-center gap-2">
      <div className="flex space-x-1">
        <div className="w-2 h-2 bg-gray-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
        <div className="w-2 h-2 bg-gray-600 rounded-full animate-bounce" style={{ animationDelay: '200ms' }}></div>
        <div className="w-2 h-2 bg-gray-600 rounded-full animate-bounce" style={{ animationDelay: '400ms' }}></div>
      </div>
      <span className="text-sm text-gray-700 font-medium">ALICE is typing...</span>
    </div>
  );

  if (isTyping) {
    return (
      <div className="flex gap-3 animate-fade-in">
        <img
          src="/src/assets/avatars/alice.svg"
          alt="ALICE"
          className="w-8 h-8 rounded-full object-cover shrink-0"
        />
        <div className="flex-1">
          <div className="bg-gray-200 rounded-lg rounded-tl-none p-3 max-w-md">
            <TypingIndicator />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex gap-3 animate-fade-in ${isUser ? 'justify-end' : ''}`}>
      {!isUser && (
        <img
          src={aliceAvatar}
          alt="ALICE"
          className="w-8 h-8 rounded-full object-cover shrink-0"
        />
      )}

      <div className={`flex-1 ${isUser ? 'flex flex-col items-end' : ''}`}>
        {/* Message Bubble */}
        <div className={`
          rounded-lg p-3 max-w-md
          ${isAlice
            ? 'bg-gray-200 rounded-tl-none'
            : 'bg-gradient-to-r from-[#FFF0DE] to-[#FFE4C4] border border-[#f7911d]/20 rounded-tr-none'
          }
        `}>
          {isTyping ? (
            <TypingIndicator />
          ) : (
            <p className="text-sm text-gray-900">{message}</p>
          )}
        </div>

        {/* Timestamp and Status */}
        <div className={`flex items-center gap-2 mt-1 text-xs text-gray-500 ${isUser ? 'justify-end' : ''}`}>
          <span>{timestamp}</span>
          {isUser && <StatusIcon />}
        </div>
      </div>

      {isUser && (
        <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center shrink-0">
          <span className="text-white text-sm font-semibold">JD</span>
        </div>
      )}
    </div>
  );
}

export default ChatMessage;

